import { useState, useEffect } from "react";
import supabaseClient from "./supabase";

export default function Landing({ onEnterApp }) {
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "lp-css";
    style.textContent = `
      /* ===== RESET / BASE ===== */
      @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap');

      .lp-root { margin:0; padding:0; background:#FAFBFC; color:#1A1D24; overflow-x:hidden; -webkit-font-smoothing:antialiased; }
      .lp-root *, .lp-root *::before, .lp-root *::after { box-sizing:border-box; margin:0; padding:0; }

      /* ===== PULSING GREEN DOT ===== */
      @keyframes lp-pulse {
        0%, 100% { opacity:1; transform:scale(1); }
        50% { opacity:0.4; transform:scale(0.85); }
      }
      .lp-dot-pulse {
        width:6px; height:6px; border-radius:50%; background:#16A34A;
        animation: lp-pulse 2.2s ease-in-out infinite;
        display:inline-block;
      }

      /* ===== STICKY NAV ===== */
      .lp-nav {
        position:sticky; top:0; z-index:100; height:54px;
        background:#1A1D24; backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px);
        border-bottom:1px solid rgba(158,107,0,0.3); border-left:3px solid #FFB000;
        display:flex; align-items:center; justify-content:space-between;
        padding:0 max(5vw,40px);
      }
      .lp-nav-brand {
        font-family:'JetBrains Mono',monospace; font-size:17px; font-weight:700; letter-spacing:0.10em; color:#F0F2F5;
      }
      .lp-nav-links { display:flex; align-items:center; gap:28px; }
      .lp-nav-link {
        font-family:'JetBrains Mono',monospace; font-size:14px; text-transform:uppercase;
        letter-spacing:0.14em; color:rgba(255,176,0,0.8); text-decoration:none;
        background:none; border:none; cursor:pointer; transition:opacity 0.15s ease;
      }
      .lp-nav-link:hover { color:#FFB000; opacity:1; }
      .lp-nav-pill {
        font-family:'JetBrains Mono',monospace; font-size:14px; font-weight:600;
        text-transform:uppercase; letter-spacing:0.1em; color:#FFB000;
        background:rgba(255,176,0,0.12); border:1px solid rgba(255,176,0,0.5);
        padding:7px 16px; border-radius:2px; cursor:pointer; text-decoration:none;
        transition:opacity 0.15s ease;
      }
      .lp-nav-pill:hover { opacity:0.82; }

      /* ===== HERO SECTION ===== */
      .lp-hero {
        background:#FAFBFC; border-left:3px solid #9E6B00; padding-top:0;
        overflow:hidden; position:relative;
      }

      /* Attitude indicator */
      .lp-atti {
        position:absolute; top:0; left:0; right:0; bottom:0; pointer-events:none; overflow:hidden;
      }
      .lp-atti-circle {
        position:absolute; left:50%; top:52%;
        transform:translate(-50%,-50%);
        width:620px; height:620px; border-radius:50%;
        background: radial-gradient(ellipse at center top, rgba(56,189,248,0.03) 0%, transparent 50%),
                    radial-gradient(ellipse at center bottom, rgba(139,90,43,0.04) 0%, transparent 50%);
        mask-image: radial-gradient(circle, rgba(0,0,0,0.7) 30%, transparent 70%);
        -webkit-mask-image: radial-gradient(circle, rgba(0,0,0,0.7) 30%, transparent 70%);
      }
      .lp-atti-horizon {
        position:absolute; left:50%; top:52%; transform:translate(-50%,-50%);
        width:600px; height:1px;
        background: linear-gradient(90deg, transparent 0%, rgba(158,107,0,0.22) 50%, transparent 100%);
      }
      .lp-atti-wing-l, .lp-atti-wing-r {
        position:absolute; top:52%; height:1px; width:50px;
        background:rgba(158,107,0,0.18); transform:translateY(-50%);
      }
      .lp-atti-wing-l { left:calc(50% - 80px); }
      .lp-atti-wing-r { left:calc(50% + 30px); }
      .lp-atti-dot {
        position:absolute; left:50%; top:52%; transform:translate(-50%,-50%);
        width:6px; height:6px; border-radius:50%; border:1px solid rgba(158,107,0,0.18);
      }
      .lp-atti-pitch {
        position:absolute; left:50%; transform:translateX(-50%);
        width:40px; height:1px; background:rgba(158,107,0,0.07);
      }
      .lp-atti-ring {
        position:absolute; left:50%; top:52%; border-radius:50%;
        border:0.5px solid rgba(158,107,0,0.04);
        transform:translate(-50%,-50%);
      }
      .lp-atti-ring-1 { width:300px; height:300px; }
      .lp-atti-ring-2 { width:450px; height:450px; }
      .lp-atti-ring-3 { width:600px; height:600px; }
      .lp-atti-bank-arc {
        position:absolute; left:50%; top:calc(52% - 260px); transform:translateX(-50%);
        width:200px; height:30px;
        border-top:0.5px solid rgba(158,107,0,0.06);
        border-radius:100px 100px 0 0;
      }
      .lp-atti-tick {
        position:absolute; width:1px; height:8px; background:rgba(158,107,0,0.08);
        top:calc(52% - 265px);
      }
      .lp-atti-tick-1 { left:calc(50% - 60px); }
      .lp-atti-tick-2 { left:calc(50% - 30px); }
      .lp-atti-tick-3 { left:calc(50% + 30px); }
      .lp-atti-tick-4 { left:calc(50% + 60px); }

      /* Annunciator strip */
      .lp-ann {
        position:relative; z-index:2;
        height:22px; background:rgba(0,0,0,0.06);
        border-bottom:1px solid rgba(158,107,0,0.18);
        display:flex; align-items:center; padding:0 max(5vw,40px);
        gap:18px; overflow:hidden;
      }
      .lp-ann-item {
        font-family:'JetBrains Mono',monospace; font-size:16px; text-transform:uppercase;
        letter-spacing:0.12em; color:rgba(158,107,0,0.85); white-space:nowrap;
      }
      .lp-ann-dim { color:#6B7280; }
      .lp-ann-green { color:#16A34A; }
      .lp-ann-right { margin-left:auto; display:flex; align-items:center; gap:8px; }

      /* Hero columns */
      .lp-hero-grid {
        position:relative; z-index:2;
        display:grid; grid-template-columns:55% 45%;
        max-width:1080px; margin:0 auto;
        padding:0 max(5vw,60px);
        min-height:calc(100vh - 76px);
        align-items:center;
      }
      .lp-hero-left { padding-right:40px; }
      .lp-eyebrow {
        font-family:'JetBrains Mono',monospace; font-size:17px;
        color:rgba(158,107,0,0.6); text-transform:uppercase;
        letter-spacing:0.2em; margin-bottom:32px;
      }
      .lp-headline {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:clamp(38px,4.5vw,58px); font-weight:800; color:#1A1D24;
        line-height:1.06; letter-spacing:-0.02em; margin-bottom:24px;
      }
      .lp-sub {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:17px; color:rgba(0,0,0,0.85); line-height:1.75;
        max-width:460px; margin-bottom:40px;
      }
      .lp-ctas { display:flex; gap:12px; margin-bottom:28px; flex-wrap:wrap; }
      .lp-cta-primary {
        font-family:'JetBrains Mono',monospace; font-size:16px; font-weight:700;
        background:#9E6B00; color:#FFFFFF; padding:13px 28px; border-radius:2px;
        border:none; cursor:pointer; transition:opacity 0.15s ease;
      }
      .lp-cta-primary:hover { opacity:0.82; }
      .lp-cta-ghost {
        font-family:'JetBrains Mono',monospace; font-size:16px; font-weight:700;
        background:transparent; color:#9E6B00; padding:13px 28px; border-radius:2px;
        border:1px solid rgba(158,107,0,0.4); cursor:pointer; transition:opacity 0.15s ease;
      }
      .lp-cta-ghost:hover { opacity:0.82; }
      .lp-trust {
        font-family:'JetBrains Mono',monospace; font-size:16px;
        color:rgba(158,107,0,0.8); letter-spacing:0.12em;
      }

      /* Terminal card */
      .lp-terminal {
        background:rgba(11,12,16,0.98); border:1px solid rgba(158,107,0,0.22);
        border-radius:5px; box-shadow:0 0 0 1px rgba(158,107,0,0.06), 0 24px 80px rgba(0,0,0,0.6);
        overflow:hidden;
      }
      .lp-term-header {
        background:rgba(158,107,0,0.07); border-bottom:1px solid rgba(158,107,0,0.15);
        padding:9px 14px; display:flex; align-items:center; justify-content:space-between;
      }
      .lp-term-dots { display:flex; gap:5px; }
      .lp-term-dot { width:6px; height:6px; border-radius:50%; }
      .lp-term-header-label {
        font-family:'JetBrains Mono',monospace; font-size:16px;
        color:rgba(158,107,0,0.85);
      }
      .lp-term-body { padding:18px 20px; font-family:'JetBrains Mono',monospace; }
      .lp-term-route { font-size:17px; color:rgba(158,107,0,0.75); }
      .lp-term-div { height:1px; background:rgba(158,107,0,0.1); margin:10px 0; }
      .lp-term-risk-row { display:flex; align-items:center; gap:12px; margin-bottom:4px; }
      .lp-term-risk-label { font-size:16px; color:rgba(158,107,0,0.8); }
      .lp-term-risk-num { font-size:34px; font-weight:700; color:#9E6B00; }
      .lp-term-risk-badge {
        font-size:16px; font-weight:700; color:#9E6B00;
        background:rgba(158,107,0,0.1); border:1px solid rgba(158,107,0,0.3);
        padding:3px 8px; border-radius:2px;
      }
      .lp-term-risk-detail { font-size:16px; color:rgba(158,107,0,0.75); margin-bottom:4px; }
      .lp-term-flag { margin-bottom:10px; }
      .lp-term-flag-main { font-size:16px; color:#E8E8E8; }
      .lp-term-flag-main span { color:#9E6B00; }
      .lp-term-flag-detail { font-size:16px; color:rgba(0,0,0,0.7); padding-left:18px; margin-top:3px; }
      .lp-term-bottom-cols { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
      .lp-term-col-label { font-size:16px; color:rgba(158,107,0,0.8); margin-bottom:4px; }
      .lp-term-col-value { font-size:16px; color:#E8E8E8; }
      .lp-term-col-value-amber { font-size:16px; color:#9E6B00; }
      .lp-term-bar {
        background:rgba(158,107,0,0.04); border-top:1px solid rgba(158,107,0,0.08);
        padding:8px 14px; font-family:'JetBrains Mono',monospace;
        font-size:16px; color:rgba(158,107,0,0.7);
      }

      /* ===== SECTION 2: STAKES ===== */
      .lp-stakes {
        background:#FAFBFC; border-left:3px solid #9E6B00;
        border-top:1px solid rgba(158,107,0,0.08);
        padding:100px max(5vw,80px);
      }
      .lp-stakes-grid {
        display:grid; grid-template-columns:42% 58%; gap:80px;
        max-width:1080px; margin:0 auto;
      }
      .lp-stat-block { margin-bottom:48px; }
      .lp-stat-num {
        font-family:'JetBrains Mono',monospace; font-size:clamp(52px,5vw,72px);
        color:#9E6B00; font-weight:700; line-height:1;
      }
      .lp-stat-label {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:16px; color:rgba(0,0,0,0.8); margin-top:6px;
      }
      .lp-stakes-para { margin-bottom:44px; }
      .lp-stakes-lead {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:18px; color:#1A1D24; font-weight:600; margin-bottom:8px;
      }
      .lp-stakes-body {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:17px; color:rgba(0,0,0,0.7); line-height:1.8;
      }

      /* ===== SECTION 3: PRODUCT ===== */
      .lp-product {
        background:rgba(158,107,0,0.015); border-top:1px solid rgba(158,107,0,0.08);
        border-bottom:1px solid rgba(158,107,0,0.08); border-left:3px solid #9E6B00;
        padding:100px max(5vw,80px);
      }
      .lp-section-label {
        text-align:center; margin-bottom:64px;
        font-family:'JetBrains Mono',monospace; font-size:17px;
        color:rgba(158,107,0,0.85); text-transform:uppercase; letter-spacing:0.2em;
      }
      .lp-product-rows { max-width:1080px; margin:0 auto; }
      .lp-product-row {
        display:grid; grid-template-columns:28% 72%; gap:40px;
        border-top:1px solid rgba(0,0,0,0.05); padding:32px 0;
      }
      .lp-product-row-rev { grid-template-columns:72% 28%; }
      .lp-product-row-rev .lp-prod-label { order:2; text-align:right; }
      .lp-product-row-rev .lp-prod-content { order:1; }
      .lp-prod-name {
        font-family:'JetBrains Mono',monospace; font-size:17px; color:#9E6B00;
        text-transform:uppercase; letter-spacing:0.14em; margin-bottom:6px;
      }
      .lp-prod-scope {
        font-family:'JetBrains Mono',monospace; font-size:16px;
        color:rgba(158,107,0,0.75);
      }
      .lp-prod-lead {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:16px; color:#1A1D24; font-weight:600; margin-bottom:8px;
      }
      .lp-prod-body {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:16px; color:rgba(0,0,0,0.7); line-height:1.75; max-width:540px;
      }
      .lp-product-end {
        border-top:1px solid rgba(0,0,0,0.05); padding-top:32px; margin-top:8px;
        text-align:center; font-family:'JetBrains Mono',monospace;
        font-size:16px; color:rgba(158,107,0,0.7); letter-spacing:0.08em;
      }

      /* ===== SECTION 5: THESIS ===== */
      .lp-thesis {
        background:rgba(158,107,0,0.015); border-top:1px solid rgba(158,107,0,0.08);
        border-bottom:1px solid rgba(158,107,0,0.08); border-left:3px solid #9E6B00;
        padding:100px max(5vw,80px);
      }
      .lp-thesis-grid {
        display:grid; grid-template-columns:1fr 1fr; gap:80px;
        max-width:1080px; margin:0 auto;
      }
      .lp-thesis-label {
        font-family:'JetBrains Mono',monospace; font-size:17px;
        color:rgba(158,107,0,0.85); text-transform:uppercase; letter-spacing:0.2em;
        margin-bottom:20px;
      }
      .lp-thesis-headline {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:28px; color:#1A1D24; font-weight:700; line-height:1.25; margin-bottom:28px;
      }
      .lp-thesis-body {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:17px; color:rgba(0,0,0,0.7); line-height:1.8; margin-bottom:20px;
      }
      .lp-thesis-metrics { display:flex; gap:36px; margin-top:36px; flex-wrap:wrap; }
      .lp-thesis-metric-num {
        font-family:'JetBrains Mono',monospace; font-size:36px; color:#9E6B00; font-weight:700;
      }
      .lp-thesis-metric-label {
        font-family:'JetBrains Mono',monospace; font-size:16px;
        color:rgba(158,107,0,0.8); text-transform:uppercase; letter-spacing:0.08em; margin-top:4px;
      }
      .lp-phase-card {
        padding:20px 22px; border-radius:3px;
        background:rgba(0,0,0,0.025); margin-bottom:16px;
      }
      .lp-phase-card-active { border-left:3px solid #9E6B00; }
      .lp-phase-card-dim { border-left:3px solid rgba(0,0,0,0.12); }
      .lp-phase-tag {
        font-family:'JetBrains Mono',monospace; font-size:16px;
        text-transform:uppercase; letter-spacing:0.1em; margin-bottom:8px;
      }
      .lp-phase-tag-active { color:#9E6B00; }
      .lp-phase-tag-dim { color:rgba(158,107,0,0.75); }
      .lp-phase-title {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:16px; color:#1A1D24; font-weight:600; margin-bottom:8px;
      }
      .lp-phase-detail {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:17px; color:rgba(0,0,0,0.8); line-height:1.6;
      }

      /* ===== SECTION 6: CTA ===== */
      .lp-cta-section {
        background:#FAFBFC; border-top:1px solid rgba(158,107,0,0.08);
        border-left:3px solid #9E6B00; padding:100px max(5vw,80px);
        text-align:center;
      }
      .lp-badges { display:flex; justify-content:center; gap:10px; margin-bottom:48px; flex-wrap:wrap; }
      .lp-badge {
        font-family:'JetBrains Mono',monospace; font-size:17px;
        color:rgba(158,107,0,0.7); background:rgba(158,107,0,0.08);
        border:1px solid rgba(158,107,0,0.2); padding:6px 14px; border-radius:2px;
      }
      .lp-cta-label {
        font-family:'JetBrains Mono',monospace; font-size:17px;
        color:rgba(158,107,0,0.85); text-transform:uppercase; letter-spacing:0.2em;
        margin-bottom:20px;
      }
      .lp-cta-headline {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:clamp(28px,3.5vw,44px); color:#1A1D24; font-weight:800;
        max-width:700px; margin:0 auto 20px;
      }
      .lp-cta-sub {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:17px; color:rgba(0,0,0,0.8); margin-bottom:36px;
      }
      .lp-cta-big-btn {
        font-family:'JetBrains Mono',monospace; font-size:17px; font-weight:700;
        background:#9E6B00; color:#FFFFFF; padding:15px 36px; border-radius:2px;
        border:none; cursor:pointer; transition:opacity 0.15s ease; margin-bottom:28px;
        display:inline-block;
      }
      .lp-cta-big-btn:hover { opacity:0.82; }
      .lp-cta-contact {
        font-family:'JetBrains Mono',monospace; font-size:17px;
        color:rgba(158,107,0,0.75);
      }

      /* ===== FOOTER ===== */
      .lp-footer {
        border-top:1px solid rgba(158,107,0,0.1); border-left:3px solid #9E6B00;
        background:rgba(11,12,16,0.98); padding:22px max(5vw,80px);
        display:flex; align-items:center; justify-content:space-between;
      }
      .lp-footer-left { display:flex; align-items:center; gap:14px; }
      .lp-footer-copy {
        font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        font-size:16px; color:rgba(0,0,0,0.3);
      }
      .lp-footer-links { display:flex; gap:20px; }
      .lp-footer-link {
        font-family:'JetBrains Mono',monospace; font-size:17px;
        color:rgba(158,107,0,0.8); text-decoration:none; background:none;
        border:none; cursor:pointer; transition:opacity 0.15s ease;
      }
      .lp-footer-link:hover { color:#9E6B00; }

      /* ===== MOBILE ===== */
      @media(max-width:768px) {
        .lp-nav-hide-mobile { display:none !important; }
        .lp-ann-hide-mobile { display:none !important; }
        .lp-atti { display:none !important; }
        .lp-hero-grid { grid-template-columns:1fr; padding:0 24px; min-height:auto; padding-top:40px; padding-bottom:60px; }
        .lp-hero-left { padding-right:0; }
        .lp-hero-right { display:none !important; }
        .lp-headline { font-size:clamp(30px,7vw,38px); }
        .lp-stakes { padding:60px 24px; }
        .lp-stakes-grid { grid-template-columns:1fr; gap:48px; }
        .lp-stat-num { font-size:52px; }
        .lp-product { padding:60px 24px; }
        .lp-product-row, .lp-product-row-rev { grid-template-columns:1fr; gap:16px; }
        .lp-product-row-rev .lp-prod-label { order:0; text-align:left; }
        .lp-product-row-rev .lp-prod-content { order:0; }
        .lp-thesis { padding:60px 24px; }
        .lp-thesis-grid { grid-template-columns:1fr; gap:48px; }
        .lp-cta-section { padding:60px 24px; }
        .lp-phase-card { width:100%; }
        .lp-badges { flex-wrap:wrap; }
        .lp-footer { flex-direction:column; text-align:center; gap:14px; }
        .lp-footer-left { flex-direction:column; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById("lp-css");
      if (el) el.remove();
    };
  }, []);

  const handleGetStarted = () => onEnterApp();

  const [showRedeem, setShowRedeem] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemName, setRedeemName] = useState("");
  const [redeemEmail, setRedeemEmail] = useState("");
  const [redeemOrg, setRedeemOrg] = useState("");
  const [redeemStatus, setRedeemStatus] = useState(null);
  const [redeemLoading, setRedeemLoading] = useState(false);

  async function handleRedeem() {
    // Read directly from DOM as fallback in case React state isn't syncing
    const nameVal = redeemName || document.getElementById("solus-redeem-name")?.value || "";
    const emailVal = redeemEmail || document.getElementById("solus-redeem-email")?.value || "";
    const orgVal = redeemOrg || document.getElementById("solus-redeem-org")?.value || "";
    const codeVal = redeemCode || document.getElementById("solus-redeem-code")?.value || "";
    if (!codeVal.trim()||!emailVal.trim()||!nameVal.trim()||!orgVal.trim()) {
      setRedeemStatus("All fields are required: name, email, organization, and code.");
      return;
    }
    setRedeemLoading(true);
    setRedeemStatus(null);
    const payload = { code: codeVal.trim().toUpperCase(), email: emailVal.trim(), name: nameVal.trim(), org: orgVal.trim() };
    console.log("Redeem payload:", JSON.stringify(payload));
    try {
      const res = await fetch("https://solus-backend-9wde.onrender.com/access/redeem", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setRedeemStatus("granted");
        localStorage.setItem("solus_waitlisted", "1");
        localStorage.setItem("solus_allowed", "1");
        // Trigger Google sign-in so user gets a full authenticated session
        setTimeout(async () => {
          await supabaseClient.auth.signInWithOAuth({
            provider: "google",
            options: { redirectTo: window.location.origin }
          });
        }, 1500);
      } else {
        setRedeemStatus(data.error || "Invalid code");
      }
    } catch {
      setRedeemStatus("Connection failed. Try again.");
    }
    setRedeemLoading(false);
  }

  return (
    <div className="lp-root">
      {/* ===== STICKY NAV ===== */}
      <nav className="lp-nav">
        <div className="lp-nav-brand">
          <span style={{ color: "#F0F2F5" }}>SOL</span>
          <span style={{ color: "#FFB000" }}>U</span>
          <span style={{ color: "#F0F2F5" }}>S</span>
        </div>
        <div className="lp-nav-links">
          <a href="/blog" className="lp-nav-link lp-nav-hide-mobile">BLOG</a>
          <button className="lp-nav-pill" onClick={handleGetStarted}>LOG IN &rarr;</button>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="lp-hero">
        {/* Attitude indicator */}
        <div className="lp-atti">
          <div className="lp-atti-circle" />
          <div className="lp-atti-horizon" />
          <div className="lp-atti-wing-l" />
          <div className="lp-atti-wing-r" />
          <div className="lp-atti-dot" />
          {[-90, -55, -28, 28, 55, 90].map((offset, i) => (
            <div key={i} className="lp-atti-pitch" style={{ top: `calc(52% + ${offset}px)` }} />
          ))}
          <div className="lp-atti-ring lp-atti-ring-1" />
          <div className="lp-atti-ring lp-atti-ring-2" />
          <div className="lp-atti-ring lp-atti-ring-3" />
          <div className="lp-atti-bank-arc" />
          <div className="lp-atti-tick lp-atti-tick-1" />
          <div className="lp-atti-tick lp-atti-tick-2" />
          <div className="lp-atti-tick lp-atti-tick-3" />
          <div className="lp-atti-tick lp-atti-tick-4" />
        </div>

        {/* Annunciator strip */}
        <div className="lp-ann">
          <span className="lp-ann-item lp-ann-dim lp-ann-hide-mobile">AIRCRAFT</span>
          <span className="lp-ann-item lp-ann-dim lp-ann-hide-mobile">WEATHER</span>
          <span className="lp-ann-item lp-ann-green lp-ann-hide-mobile">CHECKLIST COMPLETE</span>
          <span className="lp-ann-item lp-ann-green">CURRENCY GO</span>
          <span className="lp-ann-item lp-ann-dim lp-ann-hide-mobile">ANNUAL</span>
          <div className="lp-ann-right">
            <span className="lp-dot-pulse" />
            <span className="lp-ann-item" style={{ color: "#16A34A" }}>SYS LINK</span>
            <span className="lp-ann-item" style={{ color: "#16A34A" }}>LIVE</span>
          </div>
        </div>

        {/* Hero columns */}
        <div className="lp-hero-grid">
          <div className="lp-hero-left">
            <div className="lp-eyebrow">323 GA PILOTS DIED LAST YEAR. 80% WERE PREVENTABLE.</div>
            <h1 className="lp-headline">The pre-flight brief the FAA never mandated.</h1>
            <p className="lp-sub">
              SOLUS synthesizes your FAR currency, your aircraft, live FAA data, and 50 years of
              NTSB accident reports into a single risk brief. Automatically, before you touch the checklist.
            </p>
            <div className="lp-ctas">
              <button className="lp-cta-primary" onClick={handleGetStarted}>Start Free Trial &rarr;</button>
              <a href="https://apps.apple.com/us/app/solus-copilot/id6762177273" target="_blank" rel="noopener noreferrer" className="lp-cta-ghost" style={{textDecoration:"none",textAlign:"center"}}>Download on iOS</a>
            </div>

            {/* Redeem code */}
            {!showRedeem ? (
              <div style={{marginBottom:20}}>
                <button onClick={() => setShowRedeem(true)} style={{
                  background:"none", border:"none", cursor:"pointer",
                  fontFamily:"'JetBrains Mono',monospace", fontSize:10,
                  color:"rgba(158,107,0,0.55)", letterSpacing:"0.12em", textTransform:"uppercase",
                }}>
                  Have an access code? →
                </button>
              </div>
            ) : (
              <div style={{marginBottom:20, padding:"14px 16px", background:"rgba(0,0,0,0.03)", border:"1px solid rgba(158,107,0,0.2)", borderRadius:4, maxWidth:400}}>
                <div style={{fontFamily:"'JetBrains Mono',monospace", fontSize:9, color:"#9E6B00", letterSpacing:"0.18em", textTransform:"uppercase", marginBottom:10}}>
                  Redeem Access Code
                </div>
                <input
                  id="solus-redeem-name"
                  value={redeemName}
                  onChange={e => setRedeemName(e.target.value)}
                  placeholder="Full name"
                  style={{
                    width:"100%", padding:"9px 12px", marginBottom:8,
                    background:"rgba(0,0,0,0.04)", border:"1px solid rgba(158,107,0,0.15)",
                    borderRadius:3, fontFamily:"'JetBrains Mono',monospace", fontSize:11,
                    color:"#1A1D24", outline:"none",
                  }}
                />
                <input
                  id="solus-redeem-email"
                  value={redeemEmail}
                  onChange={e => setRedeemEmail(e.target.value)}
                  placeholder="Email address"
                  type="email"
                  style={{
                    width:"100%", padding:"9px 12px", marginBottom:8,
                    background:"rgba(0,0,0,0.04)", border:"1px solid rgba(158,107,0,0.15)",
                    borderRadius:3, fontFamily:"'JetBrains Mono',monospace", fontSize:11,
                    color:"#1A1D24", outline:"none",
                  }}
                />
                <input
                  id="solus-redeem-org"
                  value={redeemOrg}
                  onChange={e => setRedeemOrg(e.target.value)}
                  placeholder="Organization (e.g. USCG, ERAU, flight school)"
                  style={{
                    width:"100%", padding:"9px 12px", marginBottom:8,
                    background:"rgba(0,0,0,0.04)", border:"1px solid rgba(158,107,0,0.15)",
                    borderRadius:3, fontFamily:"'JetBrains Mono',monospace", fontSize:11,
                    color:"#1A1D24", outline:"none",
                  }}
                />
                <input
                  id="solus-redeem-code"
                  value={redeemCode}
                  onChange={e => setRedeemCode(e.target.value.toUpperCase())}
                  placeholder="Access code (e.g. SOLUS-ALPHA-001)"
                  style={{
                    width:"100%", padding:"9px 12px", marginBottom:10,
                    background:"rgba(0,0,0,0.04)", border:"1px solid rgba(158,107,0,0.2)",
                    borderRadius:3, fontFamily:"'JetBrains Mono',monospace", fontSize:12,
                    color:"#1A1D24", letterSpacing:"0.06em", outline:"none",
                  }}
                />
                <button onClick={handleRedeem} disabled={redeemLoading} style={{
                  width:"100%", padding:"10px",
                  background: redeemLoading ? "rgba(0,230,118,0.2)" : "#16A34A",
                  border:"none", borderRadius:2, cursor: redeemLoading ? "wait" : "pointer",
                  fontFamily:"'JetBrains Mono',monospace", fontSize:11, fontWeight:700,
                  color:"#FFFFFF", letterSpacing:"0.08em",
                }}>
                  {redeemLoading ? "Verifying..." : "Redeem Code"}
                </button>
                {redeemStatus === "granted" && (
                  <div style={{marginTop:8, padding:"8px 12px", background:"rgba(0,230,118,0.08)", border:"1px solid rgba(0,230,118,0.3)", borderRadius:4, fontFamily:"'JetBrains Mono',monospace", fontSize:11, color:"#16A34A"}}>
                    Access granted. Signing you in...
                  </div>
                )}
                {redeemStatus && redeemStatus !== "granted" && (
                  <div style={{marginTop:8, fontFamily:"'JetBrains Mono',monospace", fontSize:10, color:"#E84040"}}>{redeemStatus}</div>
                )}
              </div>
            )}

            <div className="lp-trust">
              &#9670; USCG &nbsp;&middot;&nbsp; &#9670; DOD &nbsp;&middot;&nbsp; &#9670; ERAU &nbsp;&middot;&nbsp; &#9670; GA PILOTS &nbsp;&middot;&nbsp; &#9670; A&amp;P MECHANICS
            </div>
          </div>

          <div className="lp-hero-right">
            <div className="lp-terminal">
              <div className="lp-term-header">
                <div className="lp-term-dots">
                  <div className="lp-term-dot" style={{ background: "#E84040" }} />
                  <div className="lp-term-dot" style={{ background: "#FFB000" }} />
                  <div className="lp-term-dot" style={{ background: "#16A34A" }} />
                </div>
                <span className="lp-term-header-label">SOLUS COPILOT &nbsp;&middot;&nbsp; PRE-DEPARTURE BRIEF</span>
              </div>
              <div className="lp-term-body">
                <div className="lp-term-route">THU 20 MAR 2026 &nbsp;&middot;&nbsp; KVNY &rarr; KSZP &nbsp;&middot;&nbsp; N44821 &nbsp;C172S</div>
                <div className="lp-term-div" />
                <div className="lp-term-risk-row">
                  <span className="lp-term-risk-label">RISK</span>
                  <span className="lp-term-risk-num">41</span>
                  <span className="lp-term-risk-badge">REVIEW</span>
                </div>
                <div className="lp-term-risk-detail">Primary: VFR day currency expires in 3 days</div>
                <div className="lp-term-div" />

                <div className="lp-term-flag">
                  <div className="lp-term-flag-main"><span>&#9670;</span>&nbsp; VFR day currency expires in 3 days</div>
                  <div className="lp-term-flag-detail">87 days since last landing &middot; FAR 61.57 &middot; 3 landings in 90 days</div>
                </div>
                <div className="lp-term-flag">
                  <div className="lp-term-flag-main"><span>&#9670;</span>&nbsp; NOTAM: Runway 16R closed. Use 16L</div>
                  <div className="lp-term-flag-detail">Effective until 2026-03-28 2300Z &middot; KVNY</div>
                </div>
                <div className="lp-term-flag">
                  <div className="lp-term-flag-main"><span>&#9670;</span>&nbsp; PIREP: Moderate turbulence 15nm NE</div>
                  <div className="lp-term-flag-detail">At 5,500ft MSL &middot; Filed 22 minutes ago</div>
                </div>

                <div className="lp-term-div" />
                <div className="lp-term-bottom-cols">
                  <div>
                    <div className="lp-term-col-label">METAR KVNY</div>
                    <div className="lp-term-col-value">VFR &middot; 270@08kt &middot; 10+SM</div>
                  </div>
                  <div>
                    <div className="lp-term-col-label">CURRENCY</div>
                    <div className="lp-term-col-value-amber">4/5 current &middot; 1 expiring</div>
                  </div>
                </div>
              </div>
              <div className="lp-term-bar">
                Live &middot; aviationweather.gov &middot; FAA NOTAM &middot; Pilot reports &middot; NTSB
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 2: STAKES ===== */}
      <section className="lp-stakes">
        <div className="lp-stakes-grid">
          <div>
            <div className="lp-stat-block">
              <div className="lp-stat-num">323</div>
              <div className="lp-stat-label">GA pilots died last year.</div>
            </div>
            <div className="lp-stat-block">
              <div className="lp-stat-num">80%</div>
              <div className="lp-stat-label">were decision failures, not mechanical.</div>
            </div>
            <div className="lp-stat-block">
              <div className="lp-stat-num">0</div>
              <div className="lp-stat-label">tools that synthesized the data before departure.</div>
            </div>
          </div>
          <div>
            <div className="lp-stakes-para">
              <div className="lp-stakes-lead">ForeFlight shows data. SOLUS reasons through it.</div>
              <div className="lp-stakes-body">
                Existing tools display METARs, NOTAMs, and charts in separate tabs and expect the
                pilot to cross-reference everything under time pressure. SOLUS synthesizes your
                currency status, aircraft squawks, active PIREPs, and 50 years of NTSB accident
                patterns into a single pre-departure risk brief. No tab switching, no missed flags.
              </div>
            </div>
            <div className="lp-stakes-para">
              <div className="lp-stakes-lead">The pre-flight brief exists for a reason.</div>
              <div className="lp-stakes-body">
                Military and airline crews receive structured threat briefs before every flight.
                GA pilots get nothing. They are expected to self-brief using scattered tools, often
                while distracted at the FBO. SOLUS brings that same structured rigor to every general
                aviation departure, automatically.
              </div>
            </div>
            <div className="lp-stakes-para">
              <div className="lp-stakes-lead">The data to prevent most of these accidents already existed.</div>
              <div className="lp-stakes-body">
                Expired currency, known airspace hazards, adverse PIREPs, recurring NTSB patterns.
                The information was there. It just was not connected to the pilot at the moment
                it mattered. SOLUS connects every signal to the pilot before engine start.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: PRODUCT ===== */}
      <section className="lp-product">
        <div className="lp-section-label">&#9670; THE SYSTEM</div>
        <div className="lp-product-rows">
          {/* Row 1: label left */}
          <div className="lp-product-row">
            <div className="lp-prod-label">
              <div className="lp-prod-name">CURRENCY DECAY</div>
              <div className="lp-prod-scope">5 FAR requirements</div>
            </div>
            <div className="lp-prod-content">
              <div className="lp-prod-lead">Tracks all five FAR requirements with days-to-expiry countdown.</div>
              <div className="lp-prod-body">
                Flight review, medical, VFR day, VFR night, and IFR currency. Each one tracked
                individually with automatic expiry math. You see exactly how many days remain for
                each requirement, and SOLUS flags anything within 30 days as a risk factor in your
                pre-departure brief.
              </div>
            </div>
          </div>

          {/* Row 2: label right */}
          <div className="lp-product-row lp-product-row-rev">
            <div className="lp-prod-content">
              <div className="lp-prod-lead">Real-time FAA data pulled automatically on every pre-flight.</div>
              <div className="lp-prod-body">
                METARs, NOTAMs, PIREPs, and SIGMETs for your departure and destination airports.
                No manual lookup required. SOLUS fetches, parses, and integrates live FAA feeds
                the moment you open your brief, so you never miss a critical advisory.
              </div>
            </div>
            <div className="lp-prod-label">
              <div className="lp-prod-name">LIVE FAA FEEDS</div>
              <div className="lp-prod-scope">METAR &middot; NOTAM &middot; PIREP &middot; SIGMET</div>
            </div>
          </div>

          {/* Row 3: label left */}
          <div className="lp-product-row">
            <div className="lp-prod-label">
              <div className="lp-prod-name">NTSB ORACLE</div>
              <div className="lp-prod-scope">50 years &middot; 847 cases</div>
            </div>
            <div className="lp-prod-content">
              <div className="lp-prod-lead">Accident pattern matching against your specific conditions.</div>
              <div className="lp-prod-body">
                SOLUS indexes 50 years of NTSB general aviation accident data and matches patterns
                against your current flight profile. Aircraft type, route, weather, time of day, and
                pilot currency all factor into a relevance score that surfaces the most applicable
                historical incidents.
              </div>
            </div>
          </div>

          {/* Row 4: label right */}
          <div className="lp-product-row lp-product-row-rev">
            <div className="lp-prod-content">
              <div className="lp-prod-lead">Pilot-reported GPS anomalies, turbulence, and airspace hazards.</div>
              <div className="lp-prod-body">
                A shared feed of real-time pilot reports covering GPS interference, unexpected
                turbulence, TFR updates, and airspace anomalies. Every report from the network
                strengthens the signal for every other pilot flying in the same region.
              </div>
            </div>
            <div className="lp-prod-label">
              <div className="lp-prod-name">AIRSPACE FEED</div>
              <div className="lp-prod-scope">Crowdsourced &middot; Real-time</div>
            </div>
          </div>

          {/* Row 5: label left */}
          <div className="lp-product-row">
            <div className="lp-prod-label">
              <div className="lp-prod-name">RISK SCORE</div>
              <div className="lp-prod-scope">Pre-departure &middot; Synthesized</div>
            </div>
            <div className="lp-prod-content">
              <div className="lp-prod-lead">Five signals. One number. CLEAR, REVIEW, or HIGH.</div>
              <div className="lp-prod-body">
                Currency status, live weather, NOTAMs, PIREPs, and NTSB pattern matches combine
                into a single composite risk score. Each factor is weighted and explained. You see
                the number, the category, and every contributing signal in one view.
              </div>
            </div>
          </div>

          <div className="lp-product-end">
            All of this fires before the pilot asks a single question.
          </div>
        </div>
      </section>

      {/* ===== SECTION 4B: FOUNDER ===== */}
      <section style={{background:'#0B0C10',borderTop:'1px solid rgba(158,107,0,0.08)',borderLeft:'3px solid #FFB000',padding:'80px max(5vw, 80px)'}}>
        <div style={{maxWidth:800,marginLeft:'max(5vw, 40px)'}}>
          <div className="lp-section-label" style={{marginBottom:28}}>THE FOUNDER</div>
          <div style={{fontFamily:"system-ui,-apple-system,sans-serif",fontSize:22,color:'#F0F2F5',fontWeight:600,lineHeight:1.5,marginBottom:20,maxWidth:680}}>
            As a 1C in the USAF, I spent years managing DV airlift and building currency dashboards for pilots at the Pentagon. Every mission had a pre-flight risk brief. It was not optional. You did not depart without it.
          </div>
          <div style={{fontFamily:"system-ui,-apple-system,sans-serif",fontSize:22,color:'#F0F2F5',fontWeight:600,lineHeight:1.5,marginBottom:20,maxWidth:680}}>
            When I got my FAA private pilot license and started flying GA on my own, that brief did not exist. I had a checklist. I had ForeFlight. Neither told me whether I should go.
          </div>
          <div style={{fontFamily:"system-ui,-apple-system,sans-serif",fontSize:22,color:'#F0F2F5',fontWeight:600,lineHeight:1.5,marginBottom:20,maxWidth:680}}>
            Most GA crashes trace back to human error and currency negligence. The data to prevent them already exists. It just lives in silos. As a machine learning engineer at Google Cloud, I have the skillset to connect those silos into something that works before departure, not after impact.
          </div>
          <div style={{fontFamily:"system-ui,-apple-system,sans-serif",fontSize:22,color:'#F0F2F5',fontWeight:600,lineHeight:1.5,marginBottom:24,maxWidth:680}}>
            That is SOLUS.
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
            {["1C · USAF VETERAN","PENTAGON DATA ENGINEER","GOOGLE CLOUD ML ENGINEER","FAA PRIVATE PILOT"].map(b=>(
              <span key={b} style={{background:'rgba(158,107,0,0.08)',border:'1px solid rgba(158,107,0,0.2)',padding:'5px 14px',borderRadius:2,display:'inline-block',fontFamily:"'JetBrains Mono',monospace",fontSize:10,color:'rgba(158,107,0,0.7)',textTransform:'uppercase',letterSpacing:'0.1em'}}>{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: INVESTMENT THESIS ===== */}
      <section className="lp-thesis">
        <div className="lp-thesis-grid">
          <div>
            <div className="lp-thesis-label">THE THESIS</div>
            <h2 className="lp-thesis-headline">
              Every pilot who uses SOLUS makes the network more valuable for every other pilot.
            </h2>
            <p className="lp-thesis-body">
              SOLUS starts as a free utility. Currency tracking, weather briefs, and risk scoring
              cost nothing. The product earns trust by being the most useful tool in a pilot's
              workflow before asking for a dollar.
            </p>
            <p className="lp-thesis-body">
              Every flight briefed, every GPS anomaly reported, every pilot report filed feeds
              the network. More data means better risk models, which means more accurate scores,
              which attracts more pilots. The data flywheel compounds with every departure.
            </p>
            <p className="lp-thesis-body">
              The long-term moat is insurance integration. Underwriters want per-flight risk data.
              SOLUS generates it passively. Pilots who brief with SOLUS become lower-risk customers,
              creating a pricing advantage that locks in both sides of the market.
            </p>
            <div className="lp-thesis-metrics">
              <div>
                <div className="lp-thesis-metric-num">700K+</div>
                <div className="lp-thesis-metric-label">GA PILOTS IN THE US</div>
              </div>
              <div>
                <div className="lp-thesis-metric-num">$0</div>
                <div className="lp-thesis-metric-label">PAID ACQ. TO DATE</div>
              </div>
              <div>
                <div className="lp-thesis-metric-num">1,500+</div>
                <div className="lp-thesis-metric-label">GPS INCIDENTS DAILY &middot; 2026</div>
              </div>
            </div>
          </div>

          <div>
            <div className="lp-phase-card lp-phase-card-active">
              <div className="lp-phase-tag lp-phase-tag-active">NOW &middot; PHASE 1 &middot; PILOT HOOK</div>
              <div className="lp-phase-title">Zero-Distraction Utility</div>
              <div className="lp-phase-detail">
                Currency tracking, weather briefing, risk scoring. Free. No account walls, no
                feature gates. The goal is daily habit formation. A pilot opens SOLUS before every
                flight because it is faster and more complete than anything else.
              </div>
            </div>
            <div className="lp-phase-card lp-phase-card-dim">
              <div className="lp-phase-tag lp-phase-tag-dim">Q3 2026 &middot; PHASE 2 &middot; REVENUE</div>
              <div className="lp-phase-title">B2B Insurance Integration</div>
              <div className="lp-phase-detail">
                Per-flight risk data sold to aviation underwriters. Pilots who brief with SOLUS
                generate structured risk profiles that insurers can price against. Lower premiums
                for pilots, better data for carriers.
              </div>
            </div>
            <div className="lp-phase-card lp-phase-card-dim">
              <div className="lp-phase-tag lp-phase-tag-dim">2027 &middot; PHASE 3 &middot; THE MOAT</div>
              <div className="lp-phase-title">The Networked Sky</div>
              <div className="lp-phase-detail">
                Every pilot on SOLUS feeds a shared situational awareness layer. GPS anomalies,
                turbulence reports, and airspace hazards reported by the network create a real-time
                picture that no single pilot could build alone. The network effect becomes the moat.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 6: CTA ===== */}
      <section className="lp-cta-section">
        <div className="lp-badges">
          <span className="lp-badge">GA PILOTS</span>
          <span className="lp-badge">USCG OFFICERS</span>
          <span className="lp-badge">DOD PILOTS</span>
          <span className="lp-badge">FLIGHT SCHOOLS</span>
          <span className="lp-badge">A&amp;P MECHANICS</span>
        </div>
        <div className="lp-cta-label">&#9670; PRIVATE BETA &middot; INVITE ONLY</div>
        <h2 className="lp-cta-headline">
          The only AI that predicts NTSB-level risks before they happen.
        </h2>
        <p className="lp-cta-sub">
          Start your free trial today. No credit card required. No spam. Just SOLUS.
        </p>
        <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
          <button className="lp-cta-big-btn" onClick={handleGetStarted}>Start Free Trial &rarr;</button>
          <a href="https://apps.apple.com/us/app/solus-copilot/id6762177273" target="_blank" rel="noopener noreferrer" className="lp-cta-big-btn" style={{textDecoration:"none",background:"transparent",border:"1px solid rgba(158,107,0,0.4)",color:"#9E6B00"}}>Download on iOS</a>
        </div>
        <div className="lp-cta-contact">
          almeena@soluscopilot.com &nbsp;&middot;&nbsp; soluscopilot.com
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="lp-footer">
        <div className="lp-footer-left">
          <span className="lp-nav-brand" style={{ fontSize: 14 }}>
            <span style={{ color: "#F0F2F5" }}>SOL</span>
            <span style={{ color: "#FFB000" }}>U</span>
            <span style={{ color: "#F0F2F5" }}>S</span>
          </span>
          <span className="lp-footer-copy">
            &copy; 2026 SOLUS Copilot Inc. &middot; AI Flight Intelligence for General Aviation
          </span>
        </div>
        <div className="lp-footer-links">
          <a href="https://apps.apple.com/us/app/solus-copilot/id6762177273" target="_blank" rel="noopener noreferrer" className="lp-footer-link">iOS App</a>
          <a href="/blog" className="lp-footer-link">Blog</a>
          <a href="/privacy" className="lp-footer-link">Privacy</a>
          <a href="/terms" className="lp-footer-link">Terms</a>
        </div>
      </footer>
    </div>
  );
}
